import posthog from 'posthog-js';
import { useEffect, useRef, useState } from 'react';

import { Song } from '~/interfaces';
import { Icon } from '~/modules/elements/akui/icon';
import { Menu } from '~/modules/elements/akui/menu';
import { NavButton, NavCheckbox, NavRemoteControl } from '~/modules/elements/nav-controls';
import GameState from '~/modules/game-engine/game-state/game-state';
import useKeyboardNav from '~/modules/hooks/use-keyboard-nav';
import { FeatureFlags } from '~/modules/utils/feature-flags';
import useFeatureFlag from '~/modules/utils/use-feature-flag';
import { InputLagSetting } from '~/routes/settings/settings-state';
import { cn } from '~/utils/cn';

interface Props {
  song: Song | null;
  onExit?: () => void;
  /** Returns to the pause-menu list (the phone's "Back" on the mirrored keyboard). */
  onBack: () => void;
  register: ReturnType<typeof useKeyboardNav>['register'];
  isUnverifiedSong?: boolean;
}

type reportTypes = 'not-in-sync' | 'too-loud' | 'too-quiet' | 'bad-lyrics';

// The box carries an inset dark ring even when filled: a focused row is itself accent, and without
// it the checked square would vanish into that fill and leave only the tick.
const IssueBox = ({ checked }: { checked: boolean }) => (
  <span
    className={cn(
      'flex size-6 items-center justify-center rounded-[0.25rem]',
      checked
        ? 'bg-active text-black shadow-[inset_0_0_0_2px_rgba(0,0,0,0.5)]'
        : 'shadow-[inset_0_0_0_2px_rgba(255,255,255,0.4)]',
    )}>
    {checked && <Icon icon="ic:baseline-check" width={18} height={18} />}
  </span>
);

export default function RateSong({ register, onExit, onBack, song, isUnverifiedSong = false }: Props) {
  const newVolumeFFEnabled = useFeatureFlag(FeatureFlags.NewVolume);
  const menuRef = useRef<null | HTMLButtonElement>(null);
  const [issues, setIssues] = useState<reportTypes[]>([]);

  const volume = newVolumeFFEnabled ? (song?.volume ?? song?.manualVolume) : song?.manualVolume;

  const toggleIssue = (issue: reportTypes) => {
    setIssues((current) => {
      let newValues = current;
      if (current.includes(issue)) {
        newValues = current.filter((i) => i !== issue);
      } else {
        newValues = [...current, issue];
      }
      if (issue === 'too-quiet') {
        newValues = newValues.filter((i) => i !== 'too-loud');
      } else if (issue === 'too-loud') {
        newValues = newValues.filter((i) => i !== 'too-quiet');
      }
      return newValues;
    });
  };

  const isSelected = (issue: reportTypes) => issues.includes(issue);

  useEffect(() => {
    menuRef.current?.focus();
  }, [menuRef]);

  const handleRate = () => {
    const song = GameState.getSong();
    const properties = {
      songId: song?.id,
      sharedSongId: song?.sharedSongId,
      songLastUpdated: song?.lastUpdate,
      inputLag: InputLagSetting.get(),
      completionPercentage: GameState.getSongCompletionProgress(),
    };

    if (isUnverifiedSong) {
      posthog.capture('rate-unverified-song', {
        issues,
        feedbackType: issues.length ? 'bad' : 'ok',
        isExternalUnverifiedSong: true,
        ...properties,
      });
    } else if (issues.length) {
      posthog.capture('rate-song', { issues, ...properties });
    }
    onExit?.();
  };

  const anySelected = issues.length > 0;
  const isTooQuietDisabled = !!volume && volume > 0.95;

  return (
    <>
      <Menu
        modal
        data-test={'rate-song-container'}
        className="2xl:max-w-[45rem]"
        title={<span className="stroke-text">Is the song OK?</span>}>
        <Menu.SubHeader className="text-md -mt-1.5 text-white/65!">
          If there&#39;s something wrong with the song, let me know so I can fix it
        </Menu.SubHeader>
        <NavCheckbox
          nav={register}
          name="button-not-in-sync"
          label="Lyrics are not in sync"
          checked={isSelected('not-in-sync')}
          onClick={() => toggleIssue('not-in-sync')}
          leftIcon={<IssueBox checked={isSelected('not-in-sync')} />}
          className="text-md"
          size={'small'}>
          <span>Lyrics are not in sync</span>
        </NavCheckbox>
        <NavCheckbox
          nav={register}
          name="button-bad-lyrics"
          label="Wrong lyrics, missing spaces etc."
          checked={isSelected('bad-lyrics')}
          onClick={() => toggleIssue('bad-lyrics')}
          leftIcon={<IssueBox checked={isSelected('bad-lyrics')} />}
          className="text-md"
          size={'small'}>
          <span>Wrong lyrics, missing spaces etc.</span>
        </NavCheckbox>
        <NavCheckbox
          nav={register}
          name="button-too-quiet"
          label="Too quiet"
          checked={isSelected('too-quiet')}
          onClick={() => toggleIssue('too-quiet')}
          disabled={isTooQuietDisabled}
          leftIcon={<IssueBox checked={isSelected('too-quiet')} />}
          className={cn('text-md', isTooQuietDisabled && 'bg-black/55! text-slate-100!')}
          size={'small'}>
          <span className="flex items-center gap-3.5">
            <span>Too quiet</span>
            {isTooQuietDisabled && (
              <span className="ml-auto text-[0.9375rem] font-normal text-white/40 normal-case">
                already as loud as it could be
              </span>
            )}
          </span>
        </NavCheckbox>
        <NavCheckbox
          nav={register}
          name="button-too-loud"
          label="Too loud"
          checked={isSelected('too-loud')}
          onClick={() => toggleIssue('too-loud')}
          leftIcon={<IssueBox checked={isSelected('too-loud')} />}
          className="text-md"
          size={'small'}>
          <span>Too loud</span>
        </NavCheckbox>
        <Menu.Divider className="my-1" />
        <NavButton
          nav={register}
          name="button-song-ok"
          remoteLabel={anySelected ? 'Submit and exit' : 'All good, exit'}
          remoteIcon="confirm"
          isDefault
          onClick={handleRate}
          ref={menuRef}>
          {anySelected ? 'Submit and exit' : 'All good, exit the song'}
        </NavButton>
        {/* Remote-only: the on-screen rate view has no back button (host uses Backspace); give the
            phone a way back to the pause-menu list. */}
        <NavRemoteControl
          nav={register}
          name="rate-song-back"
          control={{ type: 'button', label: 'Back', variant: 'back' }}
          onClick={onBack}
        />
      </Menu>
    </>
  );
}
